
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { logAssessmentOperation } from "./logging";

/**
 * Enhanced assessment ID generation with maximum uniqueness guarantees
 */
export const generateEnhancedAssessmentId = async (projektId: string, folderId: string): Promise<string> => {
  try {
    // Get current user for additional uniqueness
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id || 'anonymous';
    
    // High-precision timestamp with microseconds
    const now = Date.now();
    const microseconds = performance.now().toString().replace('.', '').padEnd(15, '0');
    const nanoRandom = Math.random().toString(36).substring(2, 15);
    
    // Generate multiple UUIDs for extreme uniqueness
    const uuid1 = uuidv4().replace(/-/g, '');
    const uuid2 = uuidv4().replace(/-/g, '');
    
    // Create ultra-unique ID with multiple entropy sources
    const uniqueId = `assessment_${projektId.substring(0, 8)}_${folderId.substring(0, 8)}_${userId.substring(0, 8)}_${now}_${microseconds}_${nanoRandom}_${uuid1.substring(0, 8)}_${uuid2.substring(0, 8)}`;
    
    logAssessmentOperation("ENHANCED_ID_GENERATED", { 
      assessmentId: uniqueId,
      timestamp: now,
      microseconds,
      projektId: projektId.substring(0, 8),
      folderId: folderId.substring(0, 8) 
    });
    
    return uniqueId;
  } catch (error) {
    logAssessmentOperation("ID_GENERATION_ERROR", { error: error.message });
    // Ultimate fallback with maximum entropy
    const fallbackTime = Date.now();
    const fallbackPerf = performance.now().toString().replace('.', '');
    const fallbackRandom1 = Math.random().toString(36).substring(2, 20);
    const fallbackRandom2 = Math.random().toString(36).substring(2, 20);
    const fallbackUuid = uuidv4().replace(/-/g, '');
    
    return `assessment_emergency_${fallbackTime}_${fallbackPerf}_${fallbackRandom1}_${fallbackRandom2}_${fallbackUuid}`;
  }
};

/**
 * Atomic ID reservation with database verification
 */
export const reserveUniqueAssessmentId = async (
  projektId: string, 
  folderId: string,
  maxAttempts: number = 10
): Promise<string> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const candidateId = await generateEnhancedAssessmentId(projektId, folderId);
      
      // Verify ID is truly unique in database
      const { data: existingAssessments, error: checkError } = await supabase
        .from("folder_risk_assessments")
        .select("assessment_id")
        .eq("assessment_id", candidateId);
      
      if (checkError) {
        throw new Error(`Database check failed: ${checkError.message}`);
      }
      
      if (existingAssessments && existingAssessments.length > 0) {
        logAssessmentOperation("ID_COLLISION_DETECTED", {
          candidateId,
          attempt,
          existingCount: existingAssessments.length
        });
        
        // Exponential backoff before retry
        const backoffDelay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        continue;
      }
      
      // Try atomic reservation by attempting insert with immediate delete
      const { data, error } = await supabase
        .from("folder_risk_assessments")
        .insert({
          assessment_id: candidateId,
          folder_id: folderId,
          project_id: projektId,
          name: `TEMP_RESERVATION_${Date.now()}`,
          type: "reservation",
          data: { 
            _isReservation: true, 
            _createdAt: new Date().toISOString(),
            _reservedBy: (await supabase.auth.getUser()).data.user?.id || 'anonymous'
          },
          created_by: (await supabase.auth.getUser()).data.user?.id,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select();
      
      if (!error && data && data.length > 0) {
        // Successfully reserved, now delete the temporary reservation
        await supabase
          .from("folder_risk_assessments")
          .delete()
          .eq("id", data[0].id);
        
        logAssessmentOperation("ID_RESERVED_SUCCESS", {
          assessmentId: candidateId,
          attempt,
          projektId,
          folderId
        });
        
        return candidateId;
      }
      
      logAssessmentOperation("ID_RESERVATION_FAILED", {
        candidateId,
        attempt,
        error: error?.message
      });
      
    } catch (error) {
      logAssessmentOperation("ID_RESERVATION_ERROR", {
        attempt,
        error: error.message
      });
      
      if (attempt === maxAttempts) {
        throw new Error(`Failed to reserve unique ID after ${maxAttempts} attempts: ${error.message}`);
      }
    }
  }
  
  throw new Error(`Failed to generate unique assessment ID after ${maxAttempts} attempts`);
};

/**
 * Guaranteed unique ID generation with fallback strategies
 */
export const generateGuaranteedUniqueAssessmentId = async (
  projektId: string, 
  folderId: string,
  maxAttempts: number = 10
): Promise<string> => {
  try {
    return await reserveUniqueAssessmentId(projektId, folderId, maxAttempts);
  } catch (error) {
    logAssessmentOperation("GUARANTEED_ID_FALLBACK", {
      error: error.message,
      projektId,
      folderId
    });
    
    // Ultimate fallback with extreme uniqueness measures
    const timestamp = Date.now();
    const perfNow = performance.now().toString().replace('.', '');
    const uuid1 = uuidv4().replace(/-/g, '');
    const uuid2 = uuidv4().replace(/-/g, '');
    const uuid3 = uuidv4().replace(/-/g, '');
    const random1 = Math.random().toString(36).substring(2, 20);
    const random2 = Math.random().toString(36).substring(2, 20);
    const userAgent = typeof window !== 'undefined' ? 
      window.navigator.userAgent.substring(0, 10).replace(/[^a-zA-Z0-9]/g, '') : 
      'server';
    
    return `assessment_ultimate_${timestamp}_${perfNow}_${uuid1}_${uuid2}_${uuid3}_${random1}_${random2}_${userAgent}`;
  }
};

/**
 * Legacy compatibility functions
 */
export const generateUniqueAssessmentId = (projektId: string, folderId: string): string => {
  const timestamp = Date.now();
  const microseconds = performance.now().toString().replace('.', '').substring(0, 10);
  const uuid = uuidv4().replace(/-/g, '').substring(0, 16);
  const randomSuffix = Math.random().toString(36).substring(2, 15);
  
  return `assessment_${projektId.substring(0, 8)}_${folderId.substring(0, 8)}_${timestamp}_${microseconds}_${uuid}_${randomSuffix}`;
};
